/**
 * Business-capacity declaration (OCP-534).
 *
 * The orboto AGB V4 (OWS-D23) Präambel B:
 *
 *   The services of orboto are directed exclusively at entrepreneurs within the
 *   meaning of Sec. 14 BGB, legal entities under public law and public-law
 *   special funds. orboto does not conclude contracts with consumers. BY
 *   PLACING AN ORDER THE CUSTOMER CONFIRMS that they are acting in the exercise
 *   of their trade, business or profession. orboto MAY VERIFY that status and
 *   MAY REFUSE OR TERMINATE an order placed by a consumer.
 *
 * Nothing in the product captured that. This module is the customer-facing
 * half: the declaration form (hosted INSIDE the order dialog, never as a second
 * modal), the read-only view of what is on file, and the prompt banner for
 * accounts that predate the decision.
 *
 * Load-bearing details:
 *
 *  - THE WORDING IS SERVED, NOT WRITTEN HERE. `GET /v1/portal/settings/
 *    business-capacity` returns the exact statement the API persists as
 *    evidence and snapshots onto every order, per portal language
 *    (`statements`, OCP-895). The form shows the sentence of the UI language
 *    and sends that language back, so the account stores exactly what was on
 *    screen. There is NO local fallback: until OCP-895 a failed probe showed a
 *    catalogue sentence the API never stored. Without a served sentence the
 *    checkbox is disabled and says why.
 *  - SINCE OCP-895 THE SIGNUP FORM COLLECTS BOTH: the company name (required
 *    there) and this declaration, ticked next to the same API-served statement
 *    (`BusinessCapacityConfirmCheckbox`, wording from the public
 *    `GET /v1/portal/auth/signup/declarations`). This form, the banner and the
 *    order-dialog step stay for accounts created before that or whose
 *    declaration is missing: for them the name becomes required at the moment
 *    a contract is concluded, which is exactly the moment this form is shown.
 *  - VAT-ID IS NOT ASKED FOR AND NEVER GATES. Small businesses
 *    (Kleinunternehmer) and non-EU customers legitimately have none. It stays
 *    an optional field under Billing and is snapshotted onto the order as
 *    evidence when present.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import i18n, { currentLocale } from '../i18n';

import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { fmtDateTime } from '../lib/format-time';

export const BUSINESS_CAPACITY_PATH = '/v1/portal/settings/business-capacity';

/** Why a new contract conclusion would be refused right now. */
export type BusinessCapacityBlockedReason =
  | 'business_capacity_missing'
  | 'company_name_missing'
  | 'business_capacity_refused';

export interface BusinessCapacityDeclaration {
  /** The wording that was ACTUALLY confirmed - may predate a reword. */
  statement: string;
  statement_version: string;
  /** OCP-895 - the language it was confirmed in; null = before OCP-895 (English). */
  statement_language?: 'de' | 'en' | null;
  confirmed_at: string;
  confirmed_by: string;
}

export interface BusinessCapacityRefusal {
  refused_at: string;
  /** Operator copy, rendered verbatim. */
  reason: string;
}

export interface BusinessCapacityResponse {
  /** The wording to SHOW - always the current constant, in `statement_language`. */
  statement: string;
  statement_version: string;
  /** OCP-895 - the language of `statement`. Optional for a rolling deploy (older API: English). */
  statement_language?: 'de' | 'en';
  /** OCP-895 - the current statement per portal language. */
  statements?: Record<'de' | 'en', { statement: string; statement_version: string }>;
  declaration: BusinessCapacityDeclaration | null;
  company_name: string | null;
  vat_id: string | null;
  can_conclude: boolean;
  blocked_reason: BusinessCapacityBlockedReason | null;
  refusal: BusinessCapacityRefusal | null;
  role: 'owner' | 'admin' | 'viewer' | 'billing-only';
}

/**
 * OCP-895 - the served sentence of `language`, or null when the API did not
 * serve one in that language (a failed probe, or an older API that only knows
 * English). Null means: do not let the customer tick anything - a sentence the
 * API would not record must never be on screen next to the checkbox.
 */
export function servedBusinessCapacityStatement(
  state: Pick<BusinessCapacityResponse, 'statement' | 'statement_language' | 'statements'> | null | undefined,
  language: 'de' | 'en',
): string | null {
  if (!state) return null;
  const served = state.statements?.[language]?.statement;
  if (served) return served;
  // older API: one English statement without a language tag
  const lang = state.statement_language ?? 'en';
  return lang === language ? state.statement : null;
}

/** Roles allowed to MAKE the declaration - it binds the account holder. */
export function canDeclareBusinessCapacity(
  role: BusinessCapacityResponse['role'] | undefined,
): boolean {
  return role === 'owner' || role === 'admin';
}

const inputCls =
  'mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm';

/**
 * The declaration form. Hosted by the order review dialog as one of its steps
 * and by the Settings page, so the wording and the validation rules cannot
 * differ between "collected at checkout" and "corrected later".
 */
export function BusinessCapacityForm({
  initial,
  submitLabel,
  cancelLabel,
  onCancel,
  onSaved,
}: {
  /** Pre-fetched state, when the host already has it (the order dialog does). */
  initial?: BusinessCapacityResponse | null;
  submitLabel: string;
  cancelLabel?: string;
  onCancel: () => void;
  onSaved: (r: BusinessCapacityResponse) => void;
}) {
  const { t } = useTranslation('business-capacity');
  // OCP-895 - the sentence of the UI language, sent back so the account stores exactly it
  const language = currentLocale();
  const [state, setState] = useState<BusinessCapacityResponse | null>(initial ?? null);
  const [companyName, setCompanyName] = useState(initial?.company_name ?? '');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pull the authoritative wording so the text the customer ticks is the text
  // that gets persisted and snapshotted onto the order.
  useEffect(() => {
    if (initial) return;
    const ac = new AbortController();
    void (async () => {
      try {
        const r = await api<BusinessCapacityResponse>(BUSINESS_CAPACITY_PATH, {
          signal: ac.signal,
        });
        setState(r);
        setCompanyName((prev) => prev || (r.company_name ?? ''));
      } catch {
        // Fall back to the constant below; the server stores its own.
      }
    })();
    return () => ac.abort();
  }, [initial]);

  const statement = servedBusinessCapacityStatement(state, language);
  const valid = companyName.trim().length > 0 && confirmed && statement !== null;

  async function submit() {
    if (saving || !valid) return;
    setSaving(true);
    setError(null);
    try {
      const r = await api<BusinessCapacityResponse>(BUSINESS_CAPACITY_PATH, {
        method: 'PUT',
        body: { company_name: companyName.trim(), confirmed: true, statement_language: language },
      });
      onSaved(r);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'business_capacity_refused') {
        setError(readRefusalReason(err.body) ?? t('form.errorRefused'));
      } else if (err instanceof ApiError && err.status === 403) {
        setError(t('form.errorRole'));
      } else if (err instanceof ApiError && err.status === 401) {
        setError(t('form.errorSession'));
      } else if (err instanceof ApiError) {
        setError(t('form.errorSaveCode', { code: err.code ?? err.status }));
      } else {
        setError(t('form.errorSave'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {error && (
        <div
          role="alert"
          className="mt-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
        >
          {error}
        </div>
      )}

      <div className="mt-4 space-y-3">
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">{t('form.companyName')}</span>
          <input
            type="text"
            autoFocus
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            maxLength={200}
            placeholder={t('form.companyPlaceholder')}
            autoComplete="organization"
            className={inputCls}
            data-testid="business-capacity-company"
          />
          <span className="mt-1 block text-xs text-gray-500">
            {t('form.companyHelp')}
          </span>
        </label>

        <BusinessCapacityConfirmCheckbox
          statement={statement}
          checked={confirmed}
          onChange={setConfirmed}
        />

        <p className="text-xs text-gray-500">
          {t('form.vatHint')}
        </p>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {cancelLabel ?? t('form.cancel')}
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!valid || saving}
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:bg-gray-300"
          data-testid="business-capacity-save"
        >
          {saving ? t('form.saving') : submitLabel}
        </button>
      </div>
    </>
  );
}

/**
 * The declaration checkbox with the verbatim statement. Shared by
 * {@link BusinessCapacityForm} and the signup form (OCP-895), so the statement a
 * customer ticks looks and reads the same wherever it is made.
 */
export function BusinessCapacityConfirmCheckbox({
  statement,
  checked,
  onChange,
}: {
  /** The API-served sentence; null = not loaded, the checkbox is disabled and says so (OCP-895). */
  statement: string | null;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const { t } = useTranslation('business-capacity');
  return (
    <label className="flex items-start gap-2 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3 text-sm">
      <input
        type="checkbox"
        checked={statement !== null && checked}
        disabled={statement === null}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5"
        data-testid="business-capacity-confirm"
      />
      {statement !== null ? (
        <span className="text-gray-700 dark:text-gray-300">{statement}</span>
      ) : (
        <span role="alert" className="text-red-700 dark:text-red-300" data-testid="business-capacity-statement-unavailable">
          {t('form.statementUnavailable')}
        </span>
      )}
    </label>
  );
}

/**
 * The refusal notice. Rendered wherever a contract conclusion is blocked by an
 * operator, with the operator's own reason - a refusal the customer cannot read
 * is a dead button, not a refusal.
 */
export function BusinessCapacityRefusalNotice({
  refusal,
}: {
  refusal: BusinessCapacityRefusal | null;
}) {
  const { t } = useTranslation('business-capacity');
  return (
    <div
      role="alert"
      className="mt-4 rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-3 text-sm text-amber-900 dark:text-amber-200"
      data-testid="business-capacity-refused"
    >
      <p className="font-medium">{t('refusal.title')}</p>
      <p className="mt-1">
        {refusal?.reason || t('refusal.fallbackReason')}
      </p>
      <p className="mt-2 text-xs">
        {t('refusal.footer')}
      </p>
    </div>
  );
}

/**
 * Prompt banner for accounts that predate the decision (OCP-534).
 *
 * They keep FULL portal access - the gate only blocks concluding a NEW contract
 * - but they must be told, otherwise the first sign they get is a blocked
 * checkout. Same quiet, informational treatment as the responsible-contact
 * banner, and it disappears the moment the declaration is on file.
 *
 * A refused account gets the amber variant instead: that state is not something
 * the customer can fix in Settings, so the banner points at support rather than
 * at a form.
 */
export function BusinessCapacityBanner() {
  const { t } = useTranslation('business-capacity');
  const { user } = useAuth();
  const [state, setState] = useState<BusinessCapacityResponse | null>(null);

  useEffect(() => {
    // Unverified accounts already carry the verification banner and cannot
    // conclude anything yet - do not stack a second banner on them.
    if (!user || !user.emailVerified) {
      setState(null);
      return;
    }
    const ac = new AbortController();
    void (async () => {
      try {
        const r = await api<BusinessCapacityResponse>(BUSINESS_CAPACITY_PATH, {
          signal: ac.signal,
        });
        setState(r);
      } catch {
        // Never let a probe failure paint a banner that might be wrong.
        setState(null);
      }
    })();
    return () => ac.abort();
  }, [user]);

  if (!state || state.can_conclude) return null;

  if (state.blocked_reason === 'business_capacity_refused') {
    return (
      <div
        className="border-b border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-6 py-2 text-sm text-amber-900 dark:text-amber-200"
        data-testid="business-capacity-banner-refused"
      >
        <div className="mx-auto max-w-5xl">
          {t('banner.refused', { reason: state.refusal?.reason ?? '' })}
        </div>
      </div>
    );
  }

  return (
    <div
      className="border-b border-sky-200 dark:border-sky-900/50 bg-sky-50 dark:bg-sky-900/30 px-6 py-2 text-sm text-sky-900 dark:text-sky-200"
      data-testid="business-capacity-banner"
    >
      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
        <span>{t('banner.text')}</span>
        <Link
          to="/settings?tab=business"
          className="shrink-0 rounded border border-sky-300 dark:border-sky-800 bg-white dark:bg-gray-900 px-2 py-1 text-xs font-medium text-sky-800 dark:text-sky-200 hover:bg-sky-100 dark:hover:bg-sky-900/40"
        >
          {t('banner.cta')}
        </Link>
      </div>
    </div>
  );
}

/**
 * Customer-facing copy for every error key the three contract-conclusion gates
 * can return (checkout create, free activation, code redemption). Exported so
 * all three call sites say the same thing - a customer who is told "HTTP 400"
 * has been told nothing.
 *
 * `business_capacity_refused` is deliberately absent: that one carries the
 * operator's own reason on the response body, which
 * `contractGateMessage()` prefers over any canned text.
 */
export const CONTRACT_GATE_ERRORS: Record<string, string> = {
  business_capacity_missing:
    'Before your next order we need your company name and a short confirmation ' +
    'that you are ordering in the exercise of your trade, business or profession.',
  company_name_missing:
    'We need the company name this contract is for before the order can be placed.',
  responsible_contact_missing:
    'Please name a responsible contact person for this account before concluding a contract.',
  // OCP-580 - the dispute circuit breaker paused new checkouts. Deliberate
  // operator-side stop, not an outage; the account itself is untouched.
  checkout_temporarily_disabled:
    'Payments are temporarily unavailable while we review recent payment activity. ' +
    'Please try again later - your account and services are not affected.',
  // OCP-661 - the catalog price of this product is being corrected on our side.
  product_price_unsynced:
    'This plan cannot be booked right now. Your order was not placed. Please try again later or contact support.',
  // OCP-991 - the discount code no longer holds at the moment of the order. The
  // customer confirmed a discounted amount, so nothing was ordered.
  discount_code_invalid: 'This discount code does not exist. Your order was not placed.',
  discount_code_expired: 'This discount code has expired. Your order was not placed.',
  discount_code_revoked: 'This discount code is no longer valid. Your order was not placed.',
  discount_code_redeemed: 'This discount code has already been used. Your order was not placed.',
  discount_code_wrong_plan: 'This discount code applies to a different plan. Your order was not placed.',
  discount_code_not_available: 'Discount codes cannot be used right now. Your order was not placed.',
  // OCP-991 - another account's open order holds the code. Never shown for the customer's own orders:
  // they are not refused. The code stays remembered, it is free again if that order is not completed.
  discount_code_in_use:
    'This discount code is currently held by another account\'s open order. Your order was not placed. If that order is not completed, the code becomes available again. You can also order without the code.',
  // OCP-851 - the service price list in force changed between summary and click; nothing was placed.
  service_prices_changed:
    'The service price list was updated while you were reviewing the order. Your order was not placed. ' +
    'Please reload the order summary and order again.',
};

/**
 * Map an error from a contract-conclusion call to customer-facing copy, or
 * null when it is not one of the Terms gates and the caller should fall back
 * to its own message.
 */
export function contractGateMessage(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  if (err.code === 'business_capacity_refused') {
    return readRefusalReason(err.body) ?? i18n.t('business-capacity:form.errorRefused');
  }
  // OCP-656: the catalogue carries the same English copy as CONTRACT_GATE_ERRORS plus the German one.
  return err.code && CONTRACT_GATE_ERRORS[err.code] ? i18n.t(`business-capacity:gate.${err.code}`, { defaultValue: CONTRACT_GATE_ERRORS[err.code] }) : null;
}

/** Read the operator's refusal reason off a 403 body without trusting its shape. */
export function readRefusalReason(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const reason = (body as { reason?: unknown }).reason;
  return typeof reason === 'string' && reason.trim().length > 0 ? reason : null;
}

/** Shared date rendering for the declaration, matching the contact surfaces. */
export function formatDeclarationDate(iso: string): string {
  return fmtDateTime(iso, iso);
}

/** Hook-free reader for hosts that only need "can this account order?". */
export function useBusinessCapacity(): {
  state: BusinessCapacityResponse | null;
  reload: () => void;
} {
  const [state, setState] = useState<BusinessCapacityResponse | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const ac = new AbortController();
    void (async () => {
      try {
        setState(
          await api<BusinessCapacityResponse>(BUSINESS_CAPACITY_PATH, { signal: ac.signal }),
        );
      } catch {
        setState(null);
      }
    })();
    return () => ac.abort();
  }, [nonce]);

  return { state, reload: useCallback(() => setNonce((n) => n + 1), []) };
}
